import { z } from 'zod';
import { IsoDate, IsoDateTime, Uuid } from './common.js';

/**
 * Coach de journée (lot BF, CDC « Support, coach de journée, mails fournisseurs », contrat gelé le 20/10/2026) :
 * réglages `settings.coach`, `GET /api/coach/today`, webhook Google Chat de chaque membre
 * (`PUT /api/me/chat-webhook`). La logique pure vit dans `packages/shared/src/coach.ts`.
 */

/** Heure murale « HH:MM » (Paris). */
export const ClockTime = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/, {
  message: 'Heure attendue au format HH:MM',
});

/** `settings.coach` : plafond de rappels, seuil de la carte repoussée, bornes de la journée. */
export const CoachSettings = z
  .object({
    /** Rappels par jour au maximum, plan du matin et bilan du soir compris. */
    maxPerDay: z.int().min(1).max(12).default(4),
    /** Une carte repoussée au moins N fois demande un choix (faire, confier, bloquer, supprimer). */
    postponeThreshold: z.int().min(1).max(20).default(3),
    dayStart: ClockTime.default('07:00'),
    dayEnd: ClockTime.default('19:00'),
  })
  .refine((s) => s.dayStart < s.dayEnd, {
    message: 'La journée doit finir après son début.',
    path: ['dayEnd'],
  });
export type CoachSettings = z.infer<typeof CoachSettings>;
export const DEFAULT_COACH: CoachSettings = {
  maxPerDay: 4,
  postponeThreshold: 3,
  dayStart: '07:00',
  dayEnd: '19:00',
};

/** Sorte d'un rappel du coach (`coach_reminders.kind`). */
export const CoachReminderKind = z.enum(['plan', 'nudge', 'evening']);
export type CoachReminderKind = z.infer<typeof CoachReminderKind>;

/** Sorte d'une carte pour le coach (durée estimée, libellés). */
export const CoachCardKind = z.enum(['call', 'followup', 'support', 'other']);
export type CoachCardKind = z.infer<typeof CoachCardKind>;

/** Une carte du jour (en retard ou pour aujourd'hui) vue par le coach. */
export const CoachCard = z.object({
  id: Uuid,
  title: z.string(),
  kind: CoachCardKind,
  /** Durée estimée en minutes. */
  minutes: z.int().min(1),
  late: z.boolean(),
  postponeCount: z.int().min(0),
});
export type CoachCard = z.infer<typeof CoachCard>;

/** Un bloc de la colonne « Ta journée » : rendez-vous ou créneau libre. */
export const CoachBlock = z.object({
  kind: z.enum(['event', 'free']),
  startAt: IsoDateTime,
  endAt: IsoDateTime,
  minutes: z.int().min(0),
  /** Titre du rendez-vous (null pour un créneau libre). */
  title: z.string().nullable(),
  state: z.enum(['past', 'now', 'future']),
  /** Cartes proposées dans ce créneau (vide pour un rendez-vous). */
  cardIds: z.array(Uuid),
  /** « Proposé : 3 appels (~30 min), 1 tâche (~15 min) » (null sans proposition). */
  summary: z.string().nullable(),
});
export type CoachBlock = z.infer<typeof CoachBlock>;

/** Bandeau du coach (écran À faire). */
export const CoachBanner = z.object({
  headline: z.string(),
  detail: z.string(),
  slotStartAt: IsoDateTime,
  slotEndAt: IsoDateTime,
  /** Début du créneau libre suivant (« Plus tard (11 h 30) »), null s'il n'y en a plus aujourd'hui. */
  laterAt: IsoDateTime.nullable(),
  /** « 11 h 30 » */
  laterLabel: z.string().nullable(),
  cardIds: z.array(Uuid),
});
export type CoachBanner = z.infer<typeof CoachBanner>;

/** Une ligne de « Rappels du jour » : envoyé ou prévu. */
export const CoachReminderLine = z.object({
  kind: CoachReminderKind,
  at: IsoDateTime,
  label: z.string(),
  status: z.enum(['sent', 'planned']),
});
export type CoachReminderLine = z.infer<typeof CoachReminderLine>;

/** `GET /api/coach/today` : la journée du membre connecté. */
export const CoachToday = z.object({
  today: IsoDate,
  now: IsoDateTime,
  settings: CoachSettings,
  /** Faux le week-end. */
  workday: z.boolean(),
  /** Lot BP : jour retenu dans les préférences du membre (`notifyDays`) ; faux : aucun rappel ce jour-là. */
  notifyToday: z.boolean(),
  /** Congé ce jour-là (absence ou événement « congé » de l'agenda) : aucun rappel. */
  onLeave: z.boolean(),
  leaveLabel: z.string().nullable(),
  blocks: z.array(CoachBlock),
  cards: z.array(CoachCard),
  counts: z.object({
    meetings: z.int().min(0),
    calls: z.int().min(0),
    supportReady: z.int().min(0),
    postponed: z.int().min(0),
    late: z.int().min(0),
    today: z.int().min(0),
  }),
  banner: CoachBanner.nullable(),
  reminders: z.array(CoachReminderLine),
  /** Lot BP : de quoi recalculer la journée dans le navigateur quand l'heure avance (`rollCoachDay`). */
  meetings: z.array(
    z.object({ title: z.string(), kind: z.string().nullable(), startAt: IsoDateTime, endAt: IsoDateTime }),
  ),
  sent: z.array(
    z.object({
      kind: CoachReminderKind,
      sentAt: IsoDateTime,
      slot: IsoDateTime.nullable(),
      label: z.string().nullable(),
    }),
  ),
});
export type CoachToday = z.infer<typeof CoachToday>;

/** Adresse d'un webhook entrant Google Chat (seul hôte accepté). */
export const CHAT_WEBHOOK_PREFIX = 'https://chat.googleapis.com/v1/spaces/';

export function isChatWebhookUrl(value: string): boolean {
  if (!value.startsWith(CHAT_WEBHOOK_PREFIX)) return false;
  try {
    const url = new URL(value);
    return url.protocol === 'https:' && url.hostname === 'chat.googleapis.com';
  } catch {
    return false;
  }
}

/**
 * `PUT /api/me/chat-webhook { url | null }` : null retire le webhook. L'adresse doit commencer par
 * `https://chat.googleapis.com/v1/spaces/` (vérifiée par la route : 422 `CHAT_WEBHOOK_INVALID`).
 */
export const ChatWebhookBody = z.object({ url: z.string().trim().max(2048).nullable() });
export type ChatWebhookBody = z.infer<typeof ChatWebhookBody>;

/** Ce que le navigateur sait du webhook : jamais l'adresse. */
export const ChatWebhookStatus = z.object({ configured: z.boolean() });
export type ChatWebhookStatus = z.infer<typeof ChatWebhookStatus>;

/** `POST /api/me/chat-webhook/test` : message de test envoyé (ou simulé en démo). */
export const ChatWebhookTestResult = z.object({ sent: z.boolean(), simulated: z.boolean() });
export type ChatWebhookTestResult = z.infer<typeof ChatWebhookTestResult>;
